/* eslint-disable camelcase */
import { invisiblyApi } from '../api/invisibly-api';
import { InvisiblyApiResponse } from "../../services/api/api-response.types";
import { CreateInsertionOrderRequest, InsertionOrder, InsertionOrderAutocompleteSuggestions, InvoiceGroup, InvoiceGroupDetails, Terms, SKU, Invoice, InvoiceLineItem, CompanyMapping, CreateCompanyMappingRequest, InvoiceGroupPreviewItem } from './billing.types';
import { APITagType } from '../api/invisibly-api.types';
import { Company } from 'src/pages/billing/types/billing.types';

const billingEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<Company[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/companies',
      }),
      transformResponse(response: InvisiblyApiResponse<Company[]>) {
        return response.item;
      },
      providesTags: [APITagType.Company],
    }),
    getTerms: builder.query<Terms[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/terms',
      }),
      transformResponse(response: InvisiblyApiResponse<Terms[]>) {
        return response.item;
      },
    }),
    getSKUs: builder.query<SKU[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/skus',
      }),
      transformResponse(response: InvisiblyApiResponse<SKU[]>) {
        return response.item;
      },
    }),
    getInsertionOrders: builder.query<InsertionOrder[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/insertion_orders',
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder[]>) {
        return response.item;
      },
      providesTags: [APITagType.InsertionOrder],
    }),
    getInvoiceGroups: builder.query<InvoiceGroup[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/invoice_groups',
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroup[]>) {
        return response.item;
      },
      providesTags: [APITagType.InvoiceGroup],
    }),
    getInvoiceGroupDetails: builder.query<InvoiceGroupDetails, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoice_groups/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroupDetails>) {
        return response.item;
      },
      providesTags: (_result, _error, id) => [{ type: APITagType.InvoiceGroup, id }],
    }),
    getInsertionOrderFields: builder.query<InsertionOrderAutocompleteSuggestions, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/insertion_orders/fields',
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrderAutocompleteSuggestions>) {
        return response.item;
      },
    }),
    createInsertionOrder: builder.mutation<InsertionOrder, CreateInsertionOrderRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/insertion_orders',
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    updateInsertionOrder: builder.mutation<InsertionOrder, { id: string; data: CreateInsertionOrderRequest }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/insertion_orders/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    getInsertionOrderPreview: builder.query<InsertionOrder, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/insertion_orders/${id}/preview`,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
    }),
    sendInsertionOrder: builder.mutation<InsertionOrder, string>({
      query: (id) => ({
        method: 'post',
        url: `b2b/billing/insertion_orders/${id}/send`,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    deleteInsertionOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `b2b/billing/insertion_orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    cloneInsertionOrder: builder.mutation<InsertionOrder, string>({
      query: (id) => ({
        url: `b2b/billing/insertion_orders/${id}/clone`,
        method: 'POST',
      }),
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    createInvoiceGroup: builder.mutation<InvoiceGroup, { month: number; year: number; isInitial?: boolean }>({
      query: ({ month, year, isInitial = false }) => ({
        method: 'post',
        url: `b2b/billing/invoice_groups?isInitial=${isInitial ? 'true' : 'false'}`,
        data: { month, year, isInitial },
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroup>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    uploadInvoiceValidationFile: builder.mutation<string, {file: File, id: string}>({
      query: ({ file, id }) => {
        const fd = new FormData();
        fd.append("file", file, file.name);
        return { url: `b2b/billing/invoice_groups/${id}/validate`, method: 'POST', data: fd };
      },
      transformResponse(response: InvisiblyApiResponse<string>) {
        return response.item; // Return first item since we expect a single ProcessResponse
      },
    }),
    updateInvoice: builder.mutation<Invoice, { id: string; data: Partial<Invoice> }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/invoices/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: APITagType.InvoiceGroup, id }],
    }),
    resetInvoice: builder.mutation<Invoice, { id: string; }>({
      query: ({ id }) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/reset`,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
    }),
    updateInvoiceStatus: builder.mutation<Invoice, { id: string; data: {invoice_status: string} }>({
      query: ({ id, data }) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/status`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: APITagType.InvoiceGroup, id }],
    }),
    syncInvoice: builder.mutation<Invoice, { id: string }>({
      query: ({ id }) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/sync`,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: APITagType.InvoiceGroup, id }],
    }),
    updateInvoiceLineItem: builder.mutation<InvoiceLineItem, { id: string; data: Partial<InvoiceLineItem> }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/invoice_line_items/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: APITagType.InvoiceGroup, id }],
    }),
    resetInvoiceLineItem: builder.mutation<InvoiceLineItem, { id: string; }>({
      query: ({ id }) => ({
        method: 'post',
        url: `b2b/billing/invoice_line_items/${id}/reset`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem>) {
        return response.item;
      },
    }),
    createInvoice: builder.mutation<Invoice, { invoice_group: string; company: string; attention_needed?: boolean }>({
      query: ({ invoice_group, company, attention_needed }) => ({
        method: 'post',
        url: 'b2b/billing/invoices',
        data: { invoice_group, company, attention_needed },
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    createInvoiceLineItem: builder.mutation<InvoiceLineItem, { name: string; invoice_group: string; invoice: string; attention_needed?: boolean }>({
      query: ({ name, invoice_group, invoice, attention_needed }) => ({
        method: 'post',
        url: 'b2b/billing/invoice_line_items',
        data: { name, invoice_group, invoice, attention_needed },
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    getCompanyMappings: builder.query<CompanyMapping[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/company_mapping',
      }),
      transformResponse(response: InvisiblyApiResponse<CompanyMapping[]>) {
        return response.item;
      },
      providesTags: [APITagType.Company],
    }),
    createCompanyMapping: builder.mutation<CompanyMapping, CreateCompanyMappingRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/company_mapping',
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<CompanyMapping>) {
        return response.item;
      },
      invalidatesTags: [APITagType.Company],
    }),
    deleteCompanyMapping: builder.mutation<void, string>({
      query: (id) => ({
        method: 'delete',
        url: `b2b/billing/company_mapping/${id}`,
      }),
      invalidatesTags: [APITagType.Company],
    }),
    getInvoiceGroupPreview: builder.query<InvoiceGroupPreviewItem[], string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoice_groups/${id}/preview`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroupPreviewItem[]>) {
        return response.item;
      },
    }),
    createMapping: builder.mutation<void, CreateCompanyMappingRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/company_mapping',
        data,
      }),
    }),
    createCampaignMapping: builder.mutation<void, CreateCompanyMappingRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/company_mapping',
        data,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetCompaniesQuery,
  useGetTermsQuery,
  useGetSKUsQuery,
  useGetInsertionOrdersQuery,
  useGetInvoiceGroupsQuery,
  useGetInvoiceGroupDetailsQuery,
  useGetInsertionOrderFieldsQuery,
  useCreateInsertionOrderMutation,
  useUploadInvoiceValidationFileMutation,
  useUpdateInsertionOrderMutation,
  useGetInsertionOrderPreviewQuery,
  useLazyGetInsertionOrderPreviewQuery,
  useSendInsertionOrderMutation,
  useDeleteInsertionOrderMutation,
  useCloneInsertionOrderMutation,
  useCreateInvoiceGroupMutation,
  useUpdateInvoiceMutation,
  useUpdateInvoiceStatusMutation,
  useSyncInvoiceMutation,
  useResetInvoiceMutation,
  useUpdateInvoiceLineItemMutation,
  useCreateInvoiceMutation,
  useCreateInvoiceLineItemMutation,
  useResetInvoiceLineItemMutation,
  useGetCompanyMappingsQuery,
  useCreateCompanyMappingMutation,
  useDeleteCompanyMappingMutation,
  useGetInvoiceGroupPreviewQuery,
  useCreateMappingMutation,
  useCreateCampaignMappingMutation,
} = billingEndpoints;